import { useState } from 'react';

import { BrandMark } from '@/components/BrandMark';
import { hueInk } from '@/components/hues';
import type { Session } from '@core/session/types';
import type { LayoutState } from './layoutState';
import { resolveMenu, type ResolvedMenuNode } from './menu';

/**
 * Navigation, in the template's sidebar.
 *
 * The markup and every class name here belong to the template — `sidebar`, `submenu`, `subdrop`,
 * `menu-arrow`, and the `mini-sidebar` / `expand-menu` body classes it selects on. What is *not*
 * the template's is where the items come from: `resolveMenu` filters the manifest against the
 * session first, so navigation stays a projection of authorization rather than a second copy of
 * it, and the sidebar cannot offer a screen the API would refuse.
 *
 * Groups are **collapsible**, which is the template's own structure — an item with an icon and a
 * chevron, opening onto bulleted children. An earlier version rendered the two levels flat, with
 * groups as section headings; it worked, but it was not this design, and it made the sidebar tall
 * enough to scroll on a laptop with only six of the eventual twenty modules built.
 *
 * One branch is open at a time. Opening a second closes the first, so the list never grows past a
 * screen no matter how many modules Phase 2 adds — which is the property the flat version lost.
 */
export function Sidebar({
  session,
  activeKey,
  onNavigate,
  layout,
}: {
  session: Session;
  activeKey: string;
  onNavigate: (key: string) => void;
  layout: LayoutState;
}) {
  const groups = resolveMenu(session);

  // Whichever group holds the active screen, so the sidebar opens showing where you are. Tracked
  // separately from the user's own toggling below.
  const activeGroup = groups.find((group) =>
    (group.children ?? []).some((child) => child.key === activeKey),
  );

  const [openKey, setOpenKey] = useState<string | null>(activeGroup?.key ?? null);
  const [trackedGroup, setTrackedGroup] = useState<string | null>(activeGroup?.key ?? null);

  // Re-open the branch owning the active screen after navigating, without fighting a manual toggle
  // the rest of the time. Adjusting state during render rather than in an effect — an effect would
  // paint the closed state first and then snap it open.
  if ((activeGroup?.key ?? null) !== trackedGroup) {
    setTrackedGroup(activeGroup?.key ?? null);

    if (activeGroup) {
      setOpenKey(activeGroup.key);
    }
  }

  return (
    <aside
      id="sidebar"
      className="sidebar"
      // Hover-to-expand only applies to the rail. Firing it in the expanded sidebar would set a
      // class that changes nothing and re-render the tree on every pointer entry.
      onMouseEnter={() => layout.miniSidebar && layout.setExpandMenu(true)}
      onMouseLeave={() => layout.setExpandMenu(false)}
    >
      <div className="sidebar-logo">
        <div className="flex items-center">
          <Wordmark />
        </div>

        <button
          id="toggle_btn"
          type="button"
          className="sidenav-toggle-btn btn border-0 p-0"
          aria-label={layout.miniSidebar ? 'Expand menu' : 'Collapse menu'}
          onClick={layout.toggleMiniSidebar}
        >
          <i className="icon icon-panel-right-open align-middle" />
        </button>

        <button
          type="button"
          className="sidebar-close"
          aria-label="Close menu"
          onClick={layout.closeMobileMenu}
        >
          <i className="icon icon-x align-middle" />
        </button>
      </div>

      {/*
        `sidebar-inner` carries its own position and overflow — see the rule vendored at the end of
        `style.css`, which is the template's and clears the logo above it. `data-simplebar` stays
        because `style.css` also selects on it; the SimpleBar library itself is skipped, since
        `overflow-y: auto` is the behaviour and the menu rarely scrolls with groups collapsed.
      */}
      <div className="sidebar-inner" data-simplebar="">
        {/*
          `pt-2` stands in for the template's section heading. Its sidebar opens with a `menu-title`
          ("Main", "Inventory"), whose top margin is what separates the divider from the first item.
          Ours has no such heading — our groups *are* the sections — so without this the first pill
          sits hard against the rule under the logo.
        */}
        <div id="sidebar-menu" className="sidebar-menu pt-2">
          <ul role="menu" aria-label="Main navigation">
            {groups.map((node) =>
              /*
                A top-level node with no children is a destination, not a heading.

                Every entry here used to be a group, which was true until `today` arrived — it
                rendered with a chevron that opened an empty submenu, so the one row everybody
                lands on looked like a branch that had lost its contents. Branching on `children`
                rather than on the key, so a second top-level screen needs nothing added here.
              */
              node.children ? (
                <Group
                  key={node.key}
                  group={node}
                  open={openKey === node.key}
                  activeKey={activeKey}
                  // Closing the open one leaves nothing open, rather than falling back to the
                  // active group — a user who just closed a branch did not ask for another to
                  // appear.
                  onToggle={() => setOpenKey((current) => (current === node.key ? null : node.key))}
                  onNavigate={onNavigate}
                  onCloseMobile={layout.closeMobileMenu}
                />
              ) : (
                <Item
                  key={node.key}
                  item={node}
                  active={node.key === activeKey}
                  topLevel
                  onNavigate={onNavigate}
                  onCloseMobile={layout.closeMobileMenu}
                />
              ),
            )}
          </ul>
        </div>
      </div>
    </aside>
  );
}

function Group({
  group,
  open,
  activeKey,
  onToggle,
  onNavigate,
  onCloseMobile,
}: {
  group: ResolvedMenuNode;
  open: boolean;
  activeKey: string;
  onToggle: () => void;
  onNavigate: (key: string) => void;
  onCloseMobile: () => void;
}) {
  const children = group.children ?? [];
  return (
    <li className="submenu">
      <a
        href="#"
        /*
          `active subdrop` together, or neither — never `active` on its own.

          The template styles `a.active`, `a:hover` and `a.subdrop` identically, as the filled slate
          pill. So marking a *closed* group active because it happens to contain the current screen
          fills it in exactly like the open one, and two groups read as open at once — which is what
          the sidebar was doing. The template's own component sets `open ? "active subdrop" : undefined`
          for the same reason. Nothing is lost: the group holding the active screen is opened
          automatically, so the two states coincide until someone deliberately closes it.
        */
        className={open ? 'active subdrop' : undefined}
        aria-expanded={open}
        onClick={(event) => {
          event.preventDefault();
          onToggle();
        }}
      >
        {/*
          The group's own colour, and only while the group is closed.

          Open, the row is the template's filled slate pill and everything on it is white; a hue
          class here would fight that and lose the contrast the fill is for. Closed, the icon is the
          only thing on the row that can carry the colour — the submenu rows below are bullets by
          design, so the group icon carries the branch. An unmapped group (Setup) takes none, which
          is the right answer for configuration.
        */}
        <i className={`${group.icon ?? 'icon icon-circle-dot'} ${open ? '' : hueInk(group.key)}`} />
        <span>{group.label}</span>
        <span className="menu-arrow" />
      </a>

      {/*
        Display is set inline, which is how the template does it. The collapsed rail reveals
        submenus on hover through CSS instead, so pinning them open here would leave one branch
        stuck open over the rail while every other opened on hover.
      */}
      <ul style={open ? { display: 'block' } : undefined}>
        {children.map((item) => (
          <Item
            key={item.key}
            item={item}
            active={item.key === activeKey}
            onNavigate={onNavigate}
            onCloseMobile={onCloseMobile}
          />
        ))}
      </ul>
    </li>
  );
}

/**
 * One navigable row, in one of three states.
 *
 * Available is a link. **Locked** — the plan does not include it — stays visible with a padlock and
 * refuses the click, because a customer who never learns a module exists never buys it; that is the
 * same reasoning `resolveMenu` encodes, and hiding it here would quietly undo it.
 *
 * **Pending** — declared but not built — is dimmed and *navigable*, which is a change. It used to
 * refuse the click too, on the grounds that there was no point promising a route that answers
 * nothing. There is now something at the other end: `ComingSoonPage` names the module and says when
 * it arrives. A dimmed row that swallows every click looks broken and teaches people the sidebar is
 * unreliable; a dimmed row that explains itself does not. Locked still refuses, because the padlock
 * and its tooltip are already the whole answer.
 *
 * Locked does not get `disabled`, and that is deliberate: a disabled element drops pointer events,
 * taking its own tooltip with it. It is inert because the click handler refuses, not because the
 * browser will not deliver the click.
 *
 * No icon here, unlike the group above it. The template's submenu rows are marked by a bullet its
 * CSS draws in `::after`; adding an icon puts a second mark on the same row and pushes the label
 * out of line with every other child.
 */
function Item({
  item,
  active,
  onNavigate,
  onCloseMobile,
  topLevel = false,
}: {
  item: ResolvedMenuNode;
  active: boolean;
  onNavigate: (key: string) => void;
  onCloseMobile: () => void;
  /**
   * A leaf sitting directly in the top list rather than inside a group.
   *
   * It draws its icon, because the collapsed rail shows icons and nothing else — a top-level row
   * without one is unidentifiable there. A leaf *inside* a group is a bullet and correctly has
   * none: the group above it carries the icon for the whole branch.
   */
  topLevel?: boolean;
}) {
  const pendingOnly = item.pending && !item.locked;

  return (
    <li>
      <a
        href={item.path ?? '#'}
        className={[
          active ? 'active' : '',
          pendingOnly ? 'opacity-55' : '',
          item.locked ? 'cursor-default' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        aria-current={active ? 'page' : undefined}
        title={
          item.locked
            ? 'Not included in your plan'
            : pendingOnly
              ? 'Coming in a later phase'
              : undefined
        }
        onClick={(event) => {
          // The href is real so the row reads as a link to a screen reader and shows its
          // destination in the status bar, but navigation is a key swap in `registry.tsx`, not a
          // page load — letting the browser follow it would reload the SPA and drop the session.
          event.preventDefault();

          if (item.locked) {
            return;
          }

          onNavigate(item.key);
          onCloseMobile();
        }}
      >
        {topLevel && (
          <i
            className={`${item.icon ?? 'icon icon-circle-dot'} ${active ? '' : hueInk(item.key)}`}
          />
        )}
        <span>{item.label}</span>

        {item.locked && (
          <i
            className="icon icon-lock ms-auto text-[12px] text-warning"
            aria-label="Not included in your plan"
          />
        )}
      </a>
    </li>
  );
}

/**
 * The wordmark.
 *
 * Hand-set rather than the template's `logo.svg`: the product has no logo yet, and shipping the
 * template's mark would put another company's branding on the shop's screen. The mark is also the
 * one thing in the chrome wearing the brand teal at full strength — everything pressable is slate,
 * so the colour stays meaning "this is us" rather than "click me".
 *
 * The three variants are what the template's CSS expects: `logo-normal` in the expanded sidebar,
 * `logo-small` in the rail, `dark-logo` under `data-theme="dark"`.
 */
function Wordmark() {
  /*
    `leading-none` is not cosmetic — without it this block overlaps the menu.

    `.sidebar-logo` sets `line-height: var(--topbar-height)`, i.e. 56px, and the template gets away
    with it because its logo is an `<img>`: Preflight makes images block-level, and a block-level
    image ignores the inherited line-height. Text spans do not. Inheriting 56px made the wordmark
    56px tall, the whole logo block 89px, and `.sidebar-inner` starts at a fixed 68px — so the
    divider and the first menu item sat 33px inside each other. It also pushed the "A" off-centre
    in its own tile, which is why the mark looked notched rather than square.
  */
  const mark = <BrandMark size={28} />;

  /*
    The layout classes go on an inner span, never on the `logo-normal` / `logo-small` / `dark-logo`
    element itself.

    The template hides two of the three with `@apply hidden` inside `@layer components`, and
    Tailwind emits `@layer utilities` *after* components — so a `flex` utility on the same element
    beats the `hidden` that is supposed to be suppressing it, and all three wordmarks render at
    once. Nothing about that is visible in the markup, and it applies to any template class whose
    job is to hide something.
  */
  const wordmark = (tone: string) => (
    <span className="flex items-center gap-2.5 leading-none">
      {mark}
      <span
        className={`text-[18px] leading-none font-semibold ${tone}`}
        style={{ fontFamily: 'var(--font-serif)' }}
      >
        AttireRent
      </span>
    </span>
  );

  return (
    <>
      <a href="#" className="logo logo-normal" onClick={preventDefault}>
        {wordmark('text-gray-900')}
      </a>

      <a href="#" className="logo-small" onClick={preventDefault}>
        {mark}
      </a>

      <a href="#" className="dark-logo" onClick={preventDefault}>
        {wordmark('text-white')}
      </a>
    </>
  );
}

/** The wordmark is not a destination — there is no dashboard yet for it to lead to. */
function preventDefault(event: React.MouseEvent) {
  event.preventDefault();
}
